import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Button, Paper, IconButton, Tooltip } from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import PokemonSprite from './PokemonSprite';
import PokemonDialog from './PokemonDialog';
import TeamDialog from './TeamDialog';  // nuevo componente de selección de equipo

const BattleField = ({ onPokemonSelect, onTeamSelectYour, onTeamSelectOpponent, pokemonList = [], onSwapSides, initialSelectedPokemon, initialYourTeam, initialOpponentTeam, teraEnabled = true }) => {
  // Estados existentes para los slots individuales...
  const [dialogOpen, setDialogOpen] = useState({
    topLeft: false,
    topRight: false,
    bottomLeft: false,
    bottomRight: false
  });
  const [selectedPokemon, setSelectedPokemon] = useState(
    initialSelectedPokemon || { topLeft: null, topRight: null, bottomLeft: null, bottomRight: null }
  );

  // Estados nuevos para equipos
  const [yourTeam, setYourTeam] = useState(initialYourTeam || []);
  const [opponentTeam, setOpponentTeam] = useState(initialOpponentTeam || []);

  // Notify parent of initial imported state
  useEffect(() => {
    if (initialSelectedPokemon) onPokemonSelect(initialSelectedPokemon);
    if (initialYourTeam?.length) onTeamSelectYour?.(initialYourTeam);
    if (initialOpponentTeam?.length) onTeamSelectOpponent?.(initialOpponentTeam);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [teamDialogOpen, setTeamDialogOpen] = useState({ your: false, opponent: false });

  // Sync active pokemon data when team changes (e.g. status update)
  useEffect(() => {
    const syncSlots = (slots, team) => {
      let changed = false;
      const updated = { ...selectedPokemon };
      slots.forEach(pos => {
        const active = selectedPokemon[pos];
        if (!active?.name) return;
        const member = team.find(p => p?.name === active.name);
        if (!member) return;
        const newStatus = member.non_volatile_status || member.nonVolatileStatus || null;
        if (newStatus !== active.nonVolatileStatus) {
          updated[pos] = { ...active, nonVolatileStatus: newStatus };
          changed = true;
        }
      });
      if (changed) {
        setSelectedPokemon(updated);
        onPokemonSelect(updated);
      }
    };
    syncSlots(['topLeft', 'topRight'], yourTeam);
    syncSlots(['bottomLeft', 'bottomRight'], opponentTeam);
  }, [yourTeam, opponentTeam]);

  const handleSwapSides = () => {
    const swapped = {
      topLeft: selectedPokemon.bottomLeft,
      topRight: selectedPokemon.bottomRight,
      bottomLeft: selectedPokemon.topLeft,
      bottomRight: selectedPokemon.topRight,
    };
    setSelectedPokemon(swapped);
    onPokemonSelect(swapped);

    const newYourTeam = [...opponentTeam];
    const newOpponentTeam = [...yourTeam];
    setYourTeam(newYourTeam);
    setOpponentTeam(newOpponentTeam);
    onTeamSelectYour?.(newYourTeam);
    onTeamSelectOpponent?.(newOpponentTeam);

    onSwapSides?.();
  };

  const handleOpenDialog = (position) => {
    const team = ['topLeft', 'topRight'].includes(position) ? yourTeam : opponentTeam;
    if (!team.some(p => p?.revealed && !p?.fainted)) return;
    setDialogOpen(prev => ({ ...prev, [position]: true }));
  };

  const handleCloseDialog = (position) => {
    setDialogOpen(prev => ({
      ...prev,
      [position]: false
    }));
  };

  const handleSelectPokemon = (position, pokemonData) => {
    const oldPokemon = selectedPokemon[position];
    const newSelectedPokemon = { ...selectedPokemon, [position]: pokemonData };
    setSelectedPokemon(newSelectedPokemon);
    onPokemonSelect(newSelectedPokemon);

    const updateTeam = (team, setTeam, callback) => {
      let updated = [...team];
      // Mark new Pokémon as revealed (revealed stays true permanently once set)
      if (pokemonData?.name) {
        updated = updated.map(p => p.name === pokemonData.name ? { ...p, revealed: true } : p);
      }
      setTeam(updated);
      callback?.(updated);
    };

    if (['topLeft', 'topRight'].includes(position)) {
      updateTeam(yourTeam, setYourTeam, onTeamSelectYour);
    }
    if (['bottomLeft', 'bottomRight'].includes(position)) {
      updateTeam(opponentTeam, setOpponentTeam, onTeamSelectOpponent);
    }
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s === 'brn' || s === 'burn') return '#c62828';
    if (s === 'par' || s === 'paralysis' || s === 'paralyze') return '#F8D030';
    if (s === 'psn' || s === 'poison') return '#A040A0';
    if (s === 'tox' || s === 'badly poisoned') return '#6A0080';
    if (s === 'slp' || s === 'sleep') return '#7B6E5D';
    if (s === 'frz' || s === 'freeze' || s === 'frozen') return '#5BC8C8';
    return '#666';
  };

  // Función para renderizar un slot de Pokémon individual (ya existente)
  const renderPokemonSlot = (position, label) => {
    const pokemon = selectedPokemon[position];
    const team = ['topLeft', 'topRight'].includes(position) ? yourTeam : opponentTeam;
    const hasTeam = team.length > 0;
    const disabled = !team.some(p => p?.revealed && !p?.fainted);
    const disabledLabel = !hasTeam ? 'Select team first' : 'Reveal a Pokémon first';
    return (
      <Paper
        elevation={3}
        onClick={() => !disabled && handleOpenDialog(position)}
        title={disabled ? disabledLabel : undefined}
        sx={{
          height: 150,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: '#1A1896',
          border: '2px solid #24CC9F',
          opacity: disabled ? 0.45 : 1,
          ...(!disabled && {
            '&:hover': {
              backgroundColor: '#221FC7',
              transform: 'scale(1.02)',
              transition: 'all 0.2s ease-in-out',
            }
          })
        }}
      >
        {pokemon ? (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            {/* Indicadores de Stats */}
            <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              {/* Stats alteradas */}
              {pokemon.stats && Object.entries(pokemon.stats).map(([stat, val]) => {
                if (stat === 'hp' || val === 0) return null;
                const isPositive = val > 0;
                return (
                  <Typography key={stat} sx={{ 
                    fontSize: '0.65rem', 
                    fontWeight: 'bold', 
                    bgcolor: isPositive ? '#2e7d32' : '#d32f2f', 
                    color: 'white', 
                    px: 0.5, 
                    borderRadius: 1,
                    textTransform: 'uppercase'
                  }}>
                    {stat} {isPositive ? `+${val}` : val}
                  </Typography>
                );
              })}
            </Box>

            {/* Status esquina izquierda: non-volatile + volatile */}
            {((pokemon.nonVolatileStatus && pokemon.nonVolatileStatus !== 'none') || (pokemon.volatileStatuses && pokemon.volatileStatuses.length > 0)) && (() => {
              const CARD_HEIGHT = 150;
              const TOP_PAD = 4;
              const BADGE_H = 22; // ~18px height + 4px gap
              const available = CARD_HEIGHT - TOP_PAD;
              const hasNonVolatile = pokemon.nonVolatileStatus && pokemon.nonVolatileStatus !== 'none';
              const usedSlots = hasNonVolatile ? 1 : 0;
              const maxVolatile = Math.max(0, Math.floor(available / BADGE_H) - usedSlots);
              const volatileList = pokemon.volatileStatuses || [];
              const shown = volatileList.slice(0, maxVolatile);
              const hidden = volatileList.length - shown.length;
              return (
                <Box sx={{ position: 'absolute', top: 4, left: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                  {hasNonVolatile && (
                    <Typography sx={{
                      fontSize: '0.65rem',
                      fontWeight: 'bold',
                      bgcolor: getStatusColor(pokemon.nonVolatileStatus),
                      color: 'white',
                      px: 0.5,
                      borderRadius: 1,
                      textTransform: 'uppercase'
                    }}>
                      {pokemon.nonVolatileStatus}
                    </Typography>
                  )}
                  {shown.map(vStatus => (
                    <Typography key={vStatus} sx={{
                      fontSize: '0.6rem',
                      fontWeight: 'bold',
                      bgcolor: '#424242',
                      color: 'white',
                      px: 0.5,
                      borderRadius: 1,
                      textTransform: 'capitalize'
                    }}>
                      {vStatus}
                    </Typography>
                  ))}
                  {hidden > 0 && (
                    <Typography sx={{
                      fontSize: '0.6rem',
                      fontWeight: 'bold',
                      bgcolor: '#424242',
                      color: 'white',
                      px: 0.5,
                      borderRadius: 1,
                    }}>
                      +{hidden}
                    </Typography>
                  )}
                </Box>
              );
            })()}

            <PokemonSprite pokemon={{ name: pokemon.name }} size={80} />
            <Typography component="p" variant="subtitle1" sx={{ mt: 1 }}>
              {pokemon.name}
            </Typography>
            {team.find(p => p.name === pokemon.name)?.item && (
              <Typography variant="body2" sx={{ mt: 0.5, color: 'gold' }}>
                {team.find(p => p.name === pokemon.name).item}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography component="p" variant="subtitle1" sx={{ color: disabled ? 'rgba(255,255,255,0.5)' : 'white' }}>
            {disabled ? disabledLabel : label}
          </Typography>
        )}
      </Paper>
    );
  };

  // Click cycles: not revealed → revealed → fainted → not revealed (max 4 revealed/fainted)
  const handleTeamMemberClick = (team, setTeam, idx, callback) => {
    const p = team[idx];
    if (!p) return;
    let updated;
    if (!p.revealed && !p.fainted) {
      const revealedCount = team.filter(m => m?.revealed || m?.fainted).length;
      if (revealedCount >= 4) return;
      updated = { ...p, revealed: true,  fainted: false };
    } else if (p.revealed && !p.fainted) {
      updated = { ...p, revealed: true,  fainted: true  };
    } else {
      updated = { ...p, revealed: false, fainted: false };
    }
    const newTeam = [...team];
    newTeam[idx] = updated;
    setTeam(newTeam);
    callback?.(newTeam);
  };

  const TERA_COLORS = {
    normal: '#A8A878', fire: '#F08030', water: '#6890F0', grass: '#78C850',
    electric: '#F8D030', ice: '#98D8D8', fighting: '#C03028', poison: '#A040A0',
    ground: '#E0C068', flying: '#A890F0', psychic: '#F85888', bug: '#A8B820',
    rock: '#B8A038', ghost: '#705898', dragon: '#7038F8', dark: '#705848',
    steel: '#B8B8D0', fairy: '#EE99AC', stellar: '#FFD700',
  };

  // Función para renderizar la visualización del equipo completo
  const renderTeam = (team, activeNames = new Set(), onMemberClick = null) => {
    return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 1 }}>
        {team.map((pokemon, idx) => {
          const isActive = activeNames.has((pokemon?.name || '').toLowerCase());
          const isFainted = !isActive && pokemon?.fainted;
          const isRevealed = isActive || pokemon?.revealed;
          const clickable = onMemberClick && !isActive;
          const teraActive = teraEnabled && (pokemon?.teraActive || pokemon?.tera_active);
          const teraType = (pokemon?.teraType || pokemon?.tera_type || '').toLowerCase();
          const teraColor = teraActive ? (TERA_COLORS[teraType] || '#FFD700') : null;
          return (
            <Box
              key={idx}
              title={
                isActive ? pokemon?.name :
                isFainted ? `${pokemon?.name} (fainted)` :
                !isRevealed ? `${pokemon?.name} (not revealed)` :
                pokemon?.name
              }
              onClick={() => clickable && onMemberClick(idx)}
              sx={{
                position: 'relative',
                cursor: clickable ? 'pointer' : 'default',
                '&:hover': clickable ? { transform: 'scale(1.1)', transition: 'transform 0.15s' } : {},
              }}
            >
              <Box sx={{
                opacity: !isRevealed && !isFainted ? 0.35 : 1,
                filter: !isRevealed && !isFainted ? 'grayscale(70%)' : 'none',
                transition: 'opacity 0.2s, filter 0.2s',
              }}>
                {pokemon && <PokemonSprite pokemon={pokemon} size={60} fainted={isFainted} />}
              </Box>
              {teraColor && (
                <Box sx={{
                  position: 'absolute', top: -6, right: -4,
                  backgroundColor: teraColor,
                  color: '#fff',
                  fontSize: '0.55rem',
                  fontWeight: 900,
                  lineHeight: 1,
                  px: 0.4,
                  py: 0.2,
                  borderRadius: '3px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  boxShadow: `0 0 4px ${teraColor}`,
                  pointerEvents: 'none',
                }}>
                  {teraType || 'TERA'}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
      {/* Your Team Section */}
      <Typography component="p" variant="h6" align="center" sx={{ mb: 2 }}>
        Your Team
      </Typography>

      {/* Botón de selección de equipo - Ahora justo debajo del título */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            setTeamDialogOpen(prev => ({ ...prev, your: true }))
          }
        >
          Select Team
        </Button>
      </Box>

      {/* Visualización del equipo - Ahora separada del botón */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        {yourTeam.some(p => p) ? (
          renderTeam(
            yourTeam,
            new Set([selectedPokemon.topLeft?.name, selectedPokemon.topRight?.name].filter(Boolean).map(n => n.toLowerCase())),
            (idx) => handleTeamMemberClick(yourTeam, setYourTeam, idx, onTeamSelectYour)
          )
        ) : (
          <Typography variant="body2" color="white">
            No team selected
          </Typography>
        )}
      </Box>

      {/* Slots individuales para Pokémon */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          {renderPokemonSlot('topLeft', 'Select Left Pokémon')}
          <PokemonDialog
            open={dialogOpen.topLeft}
            onClose={() => handleCloseDialog('topLeft')}
            position="topLeft"
            onSelectPokemon={handleSelectPokemon}
            pokemonList={pokemonList}
            teamData={yourTeam}
            excludeNames={[selectedPokemon.topRight?.name].filter(Boolean)}
            currentPokemon={selectedPokemon.topLeft}
          />
        </Grid>

        <Grid item xs={6}>
          {renderPokemonSlot('topRight', 'Select Right Pokémon')}
          <PokemonDialog
            open={dialogOpen.topRight}
            onClose={() => handleCloseDialog('topRight')}
            position="topRight"
            onSelectPokemon={handleSelectPokemon}
            pokemonList={pokemonList}
            teamData={yourTeam}
            excludeNames={[selectedPokemon.topLeft?.name].filter(Boolean)}
            currentPokemon={selectedPokemon.topRight}
          />
        </Grid>
      </Grid>

      {/* Divider with swap button */}
      <Box sx={{ position: 'relative', mb: 4 }}>
        <Box
          sx={{
            height: 4,
            backgroundColor: '#24CC9F',
            borderRadius: 2,
            width: '100%'
          }}
        />
        <Tooltip title="Swap sides">
          <IconButton
            onClick={handleSwapSides}
            size="small"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: '#1A1896',
              border: '2px solid #24CC9F',
              color: '#24CC9F',
              '&:hover': { backgroundColor: '#221FC7', color: 'white' },
            }}
          >
            <SwapVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Opponent's Team Section */}
      <Typography component="p" variant="h6" align="center" sx={{ mb: 2 }}>
        Opponent's Team
      </Typography>

      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          {renderPokemonSlot('bottomLeft', 'Select Left Pokémon')}
          <PokemonDialog
            open={dialogOpen.bottomLeft}
            onClose={() => handleCloseDialog('bottomLeft')}
            position="bottomLeft"
            onSelectPokemon={handleSelectPokemon}
            pokemonList={pokemonList}
            teamData={opponentTeam}
            excludeNames={[selectedPokemon.bottomRight?.name].filter(Boolean)}
            currentPokemon={selectedPokemon.bottomLeft}
          />
        </Grid>

        <Grid item xs={6}>
          {renderPokemonSlot('bottomRight', 'Select Right Pokémon')}
          <PokemonDialog
            open={dialogOpen.bottomRight}
            onClose={() => handleCloseDialog('bottomRight')}
            position="bottomRight"
            onSelectPokemon={handleSelectPokemon}
            pokemonList={pokemonList}
            teamData={opponentTeam}
            excludeNames={[selectedPokemon.bottomLeft?.name].filter(Boolean)}
            currentPokemon={selectedPokemon.bottomRight}
          />
        </Grid>
      </Grid>

      {opponentTeam.some(p => p) ? (
        renderTeam(
          opponentTeam,
          new Set([selectedPokemon.bottomLeft?.name, selectedPokemon.bottomRight?.name].filter(Boolean).map(n => n.toLowerCase())),
          (idx) => handleTeamMemberClick(opponentTeam, setOpponentTeam, idx, onTeamSelectOpponent)
        )
      ) : (
        <Typography variant="body2" align="center">No team selected</Typography>
      )}
      <Box sx={{ textAlign: 'center', mt: 1, mb: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            setTeamDialogOpen(prev => ({ ...prev, opponent: true }))
          }
        >
          Select Team
        </Button>
      </Box>

      {/* Diálogos para seleccionar equipos */}
      <TeamDialog
        open={teamDialogOpen.your}
        onClose={() => setTeamDialogOpen(prev => ({ ...prev, your: false }))}
        onSelectTeam={(selectedTeam) => {
          if (typeof onTeamSelectYour === 'function') onTeamSelectYour(selectedTeam);
          setYourTeam(selectedTeam);
        }}
        pokemonList={pokemonList}
        initialTeam={yourTeam}
        teraEnabled={teraEnabled}
      />
      <TeamDialog
        open={teamDialogOpen.opponent}
        onClose={() => setTeamDialogOpen(prev => ({ ...prev, opponent: false }))}
        onSelectTeam={(selectedTeam) => {
          if (typeof onTeamSelectOpponent === 'function') onTeamSelectOpponent(selectedTeam);
          setOpponentTeam(selectedTeam);
        }}
        pokemonList={pokemonList}
        initialTeam={opponentTeam}
        teraEnabled={teraEnabled}
      />
    </Box>
  );
};

export default BattleField;